import { getDirtyValues } from '@src/utils';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { SubmitButtonPropsType } from '@uikit/FormItems/constants';
import { LockIcon, PenIcon } from '@src/svg';
import ConfirmModal from '@src/modals/ConfirmModal/ConfirmModal';
import { BaseButton } from '@uikit/Button/BaseButton';

const SubmitButton: FC<SubmitButtonPropsType> = ({
  onClick,
  onCancel,
  title,
  dirtyFields,
  reset,
  disabled = false,
  style = {},
  values = {},
  maskedInputs = [],
  defaultValues,
  className = '',
  disableWhenUnchangedFields = false,
  loading,
  nonControlledFieldsChanged,
  forceDisable,
  openForm = true,
  setOpenForm,
  imageChanged,
  cancelButton,
  handleRemoveFromParent,
  disableCancelConfirm = false,
  dataTestId,
  isLocked = false,
  externalCallback,
  tooltipTitle,
}) => {
  const [isChanged, setIsChanged] = useState<boolean | undefined>(false);
  const [cancel, setCancel] = useState<boolean>(false);

  const hasChangedValue = useMemo(() => {
    if (!dirtyFields || !Object.keys(dirtyFields).length) return false;

    if (dirtyFields.timeFrame && Object.keys(dirtyFields).length === 1)
      return false;

    if (!Object.keys(values).length || !maskedInputs.length) {
      return Object.keys(dirtyFields).length > 0;
    }

    const dirtyValues = getDirtyValues({
      dirtyFields,
      values,
      maskedInputs,
      defaultValues,
    });
    return Object.keys(dirtyValues).length > 0;
  }, [dirtyFields, values]);

  useEffect(() => {
    setIsChanged(
      hasChangedValue ||
        nonControlledFieldsChanged ||
        (imageChanged && dirtyFields.image)
    );
  }, [hasChangedValue, nonControlledFieldsChanged]);

  const isDisabledButton =
    openForm && ((!isChanged && disableWhenUnchangedFields) || !!forceDisable);

  const handleCancelButton = () => {
    onCancel?.();
    handleRemoveFromParent?.();
    if (defaultValues) {
      reset?.({ ...defaultValues });
    }
    setOpenForm?.(false);
    setIsChanged(false);
    setCancel(false);
  };

  const handleOnConfirm = () => {
    handleRemoveFromParent && handleRemoveFromParent();
    setCancel(false);
    if (defaultValues) {
      reset?.({ ...defaultValues });
    }
    setIsChanged(false);
  };

  return (
    <>
      {!disableCancelConfirm && isChanged && cancel && (
        <ConfirmModal
          onConfirm={handleOnConfirm}
          onCancel={() => {
            setOpenForm?.(true);
            setCancel(false);
          }}
          message="Cancel changes without saving?"
          dataTestId={`${dataTestId}-submit-confirm`}
        />
      )}
      <div className={`submit-button-container`}>
        {cancelButton && (openForm || cancel) && (
          <BaseButton
            style={style}
            className="cancel_btn"
            onClick={handleCancelButton}
            dataTestId={`${dataTestId}-cancel-button`}
            title={cancelButton}
            variant="link"
            tooltipTitle={tooltipTitle}
          />
        )}
        <BaseButton
          tooltipTitle={tooltipTitle}
          dataTestId={dataTestId as string}
          Icon={
            isLocked ? (
              <LockIcon />
            ) : openForm || isChanged ? (
              <></>
            ) : (
              <PenIcon />
            )
          }
          style={style}
          className={`input-btn button-submit-handler ${className} ${openForm ? 'bordered' : ''} ${isChanged && 'unsaved-input-btn'}`}
          onClick={() => {
            if (isLocked) {
              return externalCallback?.();
            }
            if (!openForm) {
              setOpenForm && setOpenForm(true);
            } else {
              onClick && onClick();
            }
          }}
          disabled={isDisabledButton || disabled}
          title={openForm || isChanged ? title : 'Edit'}
          isLoading={loading}
        />
      </div>
    </>
  );
};

export default SubmitButton;
